'use client';

import { c as _c } from "react/compiler-runtime";
import { zodResolver } from '@hookform/resolvers/zod';
import type { ReactNode } from 'react';
import { useImperativeHandle, useRef } from 'react';
import type { EmptyObject, ErrorOption, FieldValues, SubmitHandler, UseFormProps, UseFormReturn } from 'react-hook-form';
import { FormProvider, useForm, useFormState } from 'react-hook-form';
import type { ZodType } from 'zod';
import { trackClick } from '@sola/tracking/components/datalayer.tsx';
import { getTextFromReactNode } from '@sola/utils/react.ts';
import type { ButtonStyleProps } from '../../atoms/button-style/button-style.tsx';
import { ButtonStyle } from '../../atoms/button-style/button-style.tsx';
import { cn } from '../../utils/helpers.ts';
import { Alert } from '../alert/alert.tsx';
type GeneralErrorType = Omit<ErrorOption, 'message'> & {
  message: ReactNode;
};

/**
 * Form's internal methods to be publically exposed to parent.
 */
export interface FormPublicMethods extends Pick<UseFormReturn, 'clearErrors' | 'formState' | 'getFieldState' | 'getValues' | 'reset' | 'resetField' | 'setError' | 'setValue' | 'trigger' | 'watch'> {
  setGeneralError: (error: GeneralErrorType, config?: {
    shouldFocus: boolean;
  }) => void;
}

/**
 * Function that creates a ref to be passed to a Form's formMethods prop.
 * This allows for parent components to have access to a form's internal methods.
 */
export const useFormMethods = () => {
  return useRef(null);
};
export const FormError: React.FC = () => {
  const $ = _c(2);
  const {
    errors
  } = useFormState();
  if (!errors?.root) {
    return null;
  }
  let t0;
  if ($[0] !== errors.root.error.message) {
    t0 = <div className="Form__error my-comfy-sm" role="alert"><Alert variant="negative" dismissible={false} message={errors.root.error.message} /></div>;
    $[0] = errors.root.error.message;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
export interface FormProps extends Omit<React.ComponentPropsWithoutRef<'form'>, 'onSubmit'> {
  /**
   * Ref for internal <form> element.
   */
  formRef?: React.Ref<HTMLFormElement> | null;
  /**
   * Ref that will be assigned form's internal methods and state.
   */
  formMethods?: React.Ref<EmptyObject | FormPublicMethods>;
  isCustomError?: boolean;
  onSubmit: SubmitHandler<FieldValues>;
  /**
   * React hook form useForm options.
   */
  options?: Omit<UseFormProps, 'resolver' | 'context'>;
  validationSchema?: ZodType;
}
/**
 * Form component.
 * It is recommended to landmark your form to provide an accessible name
 * You can do this by either providing aria-labelledby, aria-label, or <title>
 */
export const Form = t0 => {
  const $ = _c(37);
  let children;
  let className;
  let formMethods;
  let formProps;
  let formRef;
  let isCustomError;
  let onSubmit;
  let options;
  let t1;
  let validationSchema;
  if ($[0] !== t0) {
    ({
      children,
      formRef,
      formMethods,
      isCustomError,
      onSubmit,
      options,
      validationSchema,
      className,
      method: t1,
      ...formProps
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = formMethods;
    $[4] = formProps;
    $[5] = formRef;
    $[6] = isCustomError;
    $[7] = onSubmit;
    $[8] = options;
    $[9] = t1;
    $[10] = validationSchema;
  } else {
    children = $[1];
    className = $[2];
    formMethods = $[3];
    formProps = $[4];
    formRef = $[5];
    isCustomError = $[6];
    onSubmit = $[7];
    options = $[8];
    t1 = $[9];
    validationSchema = $[10];
  }
  const method = t1 === undefined ? "post" : t1;
  let t2;
  if ($[11] !== validationSchema) {
    t2 = validationSchema && {
      resolver: zodResolver(validationSchema as any)
    };
    $[11] = validationSchema;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let t3;
  if ($[13] !== options || $[14] !== t2) {
    t3 = {
      ...t2,
      mode: "onBlur",
      shouldUnregister: true,
      ...options
    };
    $[13] = options;
    $[14] = t2;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  const methods = useForm(t3);
  let t4;
  if ($[16] !== methods) {
    t4 = () => ({
      clearErrors: methods.clearErrors,
      formState: methods.formState,
      getFieldState: methods.getFieldState,
      getValues: methods.getValues,
      register: methods.register,
      reset: methods.reset,
      resetField: methods.resetField,
      setError: methods.setError,
      setGeneralError: (error, config) => {
        methods.setError("root.error", error as ErrorOption, config);
      },
      setValue: methods.setValue,
      trigger: methods.trigger,
      unregister: methods.unregister,
      watch: methods.watch
    });
    $[16] = methods;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [];
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  useImperativeHandle(formMethods, t4, t5);
  let t6;
  if ($[19] !== isCustomError) {
    t6 = !isCustomError && <FormError />;
    $[19] = isCustomError;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== methods || $[22] !== onSubmit) {
    t7 = methods.handleSubmit(onSubmit);
    $[21] = methods;
    $[22] = onSubmit;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== className) {
    t8 = cn("flex flex-col gap-comfy-sm", className);
    $[24] = className;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== children || $[27] !== formProps || $[28] !== formRef || $[29] !== method || $[30] !== t7 || $[31] !== t8) {
    t9 = <form ref={formRef} method={method} noValidate={true} onSubmit={t7} className={t8} {...formProps}>{children}</form>;
    $[26] = children;
    $[27] = formProps;
    $[28] = formRef;
    $[29] = method;
    $[30] = t7;
    $[31] = t8;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  let t10;
  if ($[33] !== methods || $[34] !== t6 || $[35] !== t9) {
    t10 = <FormProvider {...methods}>{t6}{t9}</FormProvider>;
    $[33] = methods;
    $[34] = t6;
    $[35] = t9;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  return t10;
};
export type FormSubmitProps = ButtonStyleProps & {
  disableUntilFilled?: boolean;
};
/**
 * Form's submission button, which has access to internal state
 */
export const FormSubmit: React.FC<FormSubmitProps> = t0 => {
  const $ = _c(16);
  let buttonProps;
  let children;
  let disableUntilFilled;
  let disabled;
  let t1;
  if ($[0] !== t0) {
    ({
      children,
      disabled,
      disableUntilFilled,
      fullWidth: t1,
      ...buttonProps
    } = t0);
    $[0] = t0;
    $[1] = buttonProps;
    $[2] = children;
    $[3] = disableUntilFilled;
    $[4] = disabled;
    $[5] = t1;
  } else {
    buttonProps = $[1];
    children = $[2];
    disableUntilFilled = $[3];
    disabled = $[4];
    t1 = $[5];
  }
  const fullWidth = t1 === undefined ? true : t1;
  const {
    isValid
  } = useFormState();
  let isDisabled = disabled;
  if (!isDisabled) {
    isDisabled = disableUntilFilled ? !isValid : false;
  }
  let t2;
  if ($[6] !== children) {
    t2 = () => {
      trackClick(getTextFromReactNode(children) || "Form Submit");
    };
    $[6] = children;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== children || $[9] !== t2) {
    t3 = <button onClick={t2} type="submit">{children}</button>;
    $[8] = children;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== buttonProps || $[12] !== fullWidth || $[13] !== isDisabled || $[14] !== t3) {
    t4 = <ButtonStyle fullWidth={fullWidth} {...buttonProps} disabled={isDisabled}>{t3}</ButtonStyle>;
    $[11] = buttonProps;
    $[12] = fullWidth;
    $[13] = isDisabled;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
};